import { Dialog } from "@base-ui-components/react/dialog";
import { X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, createLink, updateLink, type CheckBackend, type LinkView } from "../api";

interface LinkDialogProps {
  open: boolean;
  link: LinkView | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function LinkDialog({ open, link, onClose, onSaved }: LinkDialogProps) {
  const [name, setName] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [checkBackend, setCheckBackend] = useState<CheckBackend>("cloudflare_tcp");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = Boolean(link);
  const title = isEditing ? "Edit link" : "Add link";

  useEffect(() => {
    if (!open) return;
    setName(link?.name ?? "");
    setOriginalUrl(link?.originalUrl ?? "");
    setEnabled(link?.enabled ?? true);
    setCheckBackend(link?.checkBackend ?? "cloudflare_tcp");
    setError(null);
    setIsSaving(false);
  }, [open, link?.id, link?.name, link?.originalUrl, link?.enabled, link?.checkBackend]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    setError(null);
    try {
      const input = {
        name,
        originalUrl,
        enabled,
        checkBackend
      };
      if (link) {
        await updateLink(link.id, input);
      } else {
        await createLink(input);
      }
      onClose();
      await onSaved();
    } catch (requestError) {
      setError(readRequestError(requestError, "Failed to save link"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Backdrop className="dialog-backdrop" />
        <Dialog.Viewport className="dialog-viewport">
          <Dialog.Popup className="dialog-popup">
            <div className="dialog-header">
              <div>
                <Dialog.Title className="dialog-title">{title}</Dialog.Title>
              </div>
              <Dialog.Close className="dialog-close" aria-label="Close">
                <X aria-hidden="true" className="h-4 w-4" />
              </Dialog.Close>
            </div>

            <form className="mt-5 grid gap-4" onSubmit={handleSubmit}>
              <label className="form-field">
                <span className="form-label">Display name</span>
                <input
                  className="form-input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={isSaving}
                  required
                  autoComplete="off"
                />
              </label>

              <label className="form-field">
                <span className="form-label">Full subscription URL</span>
                <input
                  className="form-input"
                  type="url"
                  value={originalUrl}
                  onChange={(event) => setOriginalUrl(event.target.value)}
                  disabled={isSaving}
                  required
                  autoComplete="off"
                />
              </label>

              <label className="flex items-center gap-3 text-sm font-medium text-[#d8d0c8]">
                <input
                  className="form-checkbox"
                  type="checkbox"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={isSaving}
                />
                Enabled
              </label>

              <label className="form-field">
                <span className="form-label">Check mode</span>
                <select
                  className="form-input"
                  value={checkBackend}
                  onChange={(event) => setCheckBackend(event.target.value as CheckBackend)}
                  disabled={isSaving}
                >
                  <option value="cloudflare_tcp">Cloudflare TCP</option>
                  <option value="local_agent_tcp">Local Agent TCP</option>
                </select>
              </label>

              {error ? (
                <div role="alert" className="inline-error">
                  {error}
                </div>
              ) : null}

              <div className="dialog-actions">
                <Dialog.Close className="secondary-button" type="button" disabled={isSaving}>
                  Cancel
                </Dialog.Close>
                <button className="accent-button" type="submit" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function readRequestError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}
